import { of, from } from 'rxjs';
import { mergeMap, withLatestFrom, map, debounceTime, catchError } from 'rxjs/operators';
import { combineEpics, ofType } from 'redux-observable';
import {
  settingsSetAppSettings,
  settingsBackupSaved,
  settingsBackupNextStep,
  settingsBackupRestored,
  settingsRestoreNextStep,
  types,
} from './settings.redux';
import { types as spectreAppTypes } from 'components/spectreApp/spectreApp.redux';
import { fileSystem } from 'components/fileSystem';
import * as R from 'ramda';
import { getDatabaseName } from '../../utils.js';
import { getAppConfig } from '../../appConfig';

const settingsSaveBackupEpic = (action$, state$) =>
  action$.pipe(
    ofType(types.settingsSaveBackup),
    withLatestFrom(state$),
    mergeMap(([, state]) => {
      console.log('Preparing backup');
      const { prepareBackup } = fileSystem();
      console.log(state);
      const databaseName = getDatabaseName(state.spectreApp.databaseName, getAppConfig().databaseVersion);

      return from(prepareBackup(databaseName)).pipe(
        map(backupInfo => settingsBackupNextStep({ backupInfo })),
        catchError(_ => of(settingsBackupSaved()))
      );
    })
  );

const settingsBackupStepsEpic = (action$, state$) =>
  action$.pipe(
    ofType(types.settingsBackupNextStep),
    withLatestFrom(state$),
    mergeMap(([{ payload }, state]) => {
      const backupStep = state.settings.backupStep;
      const backupInfo = payload.backupInfo;
      console.log('Generating backup step', backupStep);

      switch (backupStep) {
        case 2:
          const { zipBackup } = fileSystem();
          return from(zipBackup(backupInfo)).pipe(
            map(backupFile => settingsBackupNextStep({ backupFile })),
            catchError(_ => of(settingsBackupSaved()))
          );
        case 3:
          const { saveFileToDropbox, DROPBOX_APP_KEY } = fileSystem();
          const { backupFile } = payload;
          return from(saveFileToDropbox(backupFile, DROPBOX_APP_KEY)).pipe(
            map(() => settingsBackupSaved()),
            catchError(_ => of(settingsBackupSaved()))
          );
      }
    })
  );

const settingsRestoreBackupEpic = (action$, state$) =>
  action$.pipe(
    ofType(types.settingsRestoreBackup),
    mergeMap(() => {
      const { chooseFile, cacheRestoreFile } = fileSystem();

      const prepareRestore = R.pipeP(
        chooseFile,
        restoreFileUrl => cacheRestoreFile(restoreFileUrl)
      );

      return from(prepareRestore(['zip'])).pipe(
        map(restoreFileCache => settingsRestoreNextStep({ restoreFileCache })),
        catchError(_ => of(settingsBackupRestored()))
      );
    })
  );

const settingsRestoreStepsEpic = (action$, state$) =>
  action$.pipe(
    ofType(types.settingsRestoreNextStep),
    withLatestFrom(state$),
    mergeMap(([{ payload }, state]) => {
      const restoreStep = state.settings.restoreStep;
      const restoreFileCache = payload.restoreFileCache;
      const restoreFile = payload.restoreFile;

      switch (restoreStep) {
        case 2:
          const { unzipRestore } = fileSystem();
          return from(unzipRestore(restoreFileCache.url)).pipe(
            map(
              restoreFile => settingsRestoreNextStep({ restoreFile, restoreFileCache }),
              catchError(_ => of(settingsBackupRestored()))
            )
          );
        case 3:
          const { restoreData } = fileSystem();
          return from(restoreData(restoreFile)).pipe(
            map(
              () => settingsRestoreNextStep({ restoreFile, restoreFileCache }),
              catchError(_ => of(settingsBackupRestored()))
            )
          );
        case 4:
          const { restoreImages } = fileSystem();
          return from(restoreImages(restoreFile)).pipe(
            map(
              () => settingsRestoreNextStep({ restoreFile, restoreFileCache }),
              catchError(_ => of(settingsBackupRestored()))
            )
          );
        case 5:
          const { removeFile } = fileSystem();
          return from(removeFile(restoreFileCache.url)).pipe(
            map(() => {
              setTimeout(() => {
                location.reload();
              }, 500);
              return settingsBackupRestored();
            })
          );
      }
    })
  );

const settingsHandleFormEpic = action$ =>
  action$.pipe(
    ofType(types.settingsHandleForm),
    debounceTime(500),
    mergeMap(({ payload: { appSettings } }) => {
      window.localStorage.setItem('spectre_user_settings', JSON.stringify(appSettings));
      return of(settingsSetAppSettings({ appSettings: appSettings }));
    })
  );

const settingsEpic = action$ =>
  action$.pipe(
    ofType(spectreAppTypes.spectreappApplicationStart),
    mergeMap(() => {
      const savedSettings = window.localStorage.getItem('spectre_user_settings');
      return of(settingsSetAppSettings({ appSettings: JSON.parse(savedSettings) }));
    })
  );

export default combineEpics(
  settingsEpic,
  settingsHandleFormEpic,
  settingsSaveBackupEpic,
  settingsBackupStepsEpic,
  settingsRestoreBackupEpic,
  settingsRestoreStepsEpic
);
